import { useLazyQuery } from '@apollo/client';
import {
	AutocompleteFilterDefinitionType,
	Chip,
	FilterMenuOutputType,
	SelectFilterDefinitionType,
	TableColumnType,
	TableOrderByType,
} from '@elipssolution/harfang';
import { useCallback, useMemo } from 'react';

import InheritChip from '../../../../components/InheritChip';
import LinkIcon from '../../../../components/LinkIcon';
import SettingsTable from '../../../../components/SettingsTable';
import { CustomerFileWithDomainTypeAndIsDashboardInherited } from '../../../../types/customerFile';
import { DomainType } from '../../../../types/domain';
import {
	FETCH_CUSTOMER_FILES_WITH_DOMAIN_AND_IS_DASHBOARD_INHERITED,
	FetchCustomerFilesWithDomainAndIsDashboardInheritedType,
} from '../../../api/customerFile';
import { FETCH_USED_DOMAINS, FetchUsedDomainsType } from '../../../api/domain';

type WidgetCustomerFilesFilterType = {
	isDashboardInherited: SelectFilterDefinitionType<boolean>;
	domain: AutocompleteFilterDefinitionType<DomainType>;
};

const columns: TableColumnType<CustomerFileWithDomainTypeAndIsDashboardInherited>[] = [
	{
		title: 'Niveau',
		key: 'isInherited',
		field: 'isDashboardInherited',
		render: ({ isDashboardInherited }) => (
			<InheritChip
				parentLabel="Domaine"
				childLabel="Dossier"
				inherited={isDashboardInherited}
				override={!isDashboardInherited}
			/>
		),
		width: 150,
		flexGrow: 0,
	},
	{
		field: 'code',
		flexGrow: 0,
		key: 'code',
		sortable: true,
		title: 'Code',
		width: 80,
	},
	{
		field: 'name',
		key: 'name',
		sortable: true,
		title: 'Nom du dossier',
		width: 400,
	},
	{
		key: 'chip',
		render: ({ domain: { isDefault, name } }) => <Chip label={name} color={isDefault ? 'info' : 'default'} />,
		title: 'Domaine',
		width: 150,
	},
	{
		flexGrow: 0,
		key: 'navigate',
		render: () => <LinkIcon />,
		width: 24,
	},
];

type CustomerFilesTableSectionProps = {
	onCustomerFileSelection?: (customerFile: CustomerFileWithDomainTypeAndIsDashboardInherited) => void;
};

const CustomerFilesTableSection = ({ onCustomerFileSelection }: CustomerFilesTableSectionProps) => {
	const [fetchCustomerFiles] = useLazyQuery<FetchCustomerFilesWithDomainAndIsDashboardInheritedType>(
		FETCH_CUSTOMER_FILES_WITH_DOMAIN_AND_IS_DASHBOARD_INHERITED,
	);

	const dataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<CustomerFileWithDomainTypeAndIsDashboardInherited>,
			filters?: FilterMenuOutputType<WidgetCustomerFilesFilterType>,
		): Promise<{ count: number; items: CustomerFileWithDomainTypeAndIsDashboardInherited[] }> => {
			const { field, order } = orderBy || {};

			const { domain, ...restFilters } = filters ?? {};

			const { data, error } = await fetchCustomerFiles({
				variables: {
					filter: {
						...restFilters,
						...(domain && {
							domainId: { eq: domain.eq.id },
						}),
					},
					...(orderBy && { orderBy: { field, order } }),
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				customerFilesWithIsDashboardInherited: { count = 0, items = [] },
			} = data ?? {
				customerFilesWithIsDashboardInherited: {},
			};

			return {
				count,
				items,
			};
		},
		[fetchCustomerFiles],
	);

	const [fetchUsedDomains] = useLazyQuery<FetchUsedDomainsType>(FETCH_USED_DOMAINS);

	const usedDomainsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: DomainType[];
		}> => {
			const { data, error } = await fetchUsedDomains({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				usedDomains: { count = 0, items = [] },
			} = data ?? {
				usedDomains: {},
			};

			return {
				count,
				items,
			};
		},
		[fetchUsedDomains],
	);
	const filters: WidgetCustomerFilesFilterType = useMemo(
		() => ({
			isDashboardInherited: {
				label: 'Hérité du domaine',
				type: 'select',
				options: [true, false],
				renderOption: (value) => (value ? 'Oui' : 'Non'),
				renderValue: (value) => (value ? 'Oui' : 'Non'),
			},
			domain: {
				type: 'autocomplete',
				label: 'Domaine',
				dataSource: usedDomainsDataSource,
				getOptionLabel: ({ name }) => name,
			},
		}),
		[usedDomainsDataSource],
	);

	return (
		<SettingsTable<CustomerFileWithDomainTypeAndIsDashboardInherited, WidgetCustomerFilesFilterType>
			dataSource={dataSource}
			onRowClick={onCustomerFileSelection}
			tableColumns={columns}
			filters={filters}
			title="Dossiers clients"
		/>
	);
};

export default CustomerFilesTableSection;
